import React from 'react';
import { NextPage } from 'next';
import { FEATURED_GUIDES_QUERY, GUIDE_QUERY } from '../client/graphql/queries/guide';
import { slugIt, buildArticleURLObject } from '../client/utils/links';
import { buildNotFound, buildPageGone } from '../client/utils/error/http/httpErrors';
import { assertNotNullOrUndefined } from '../client/utils/error/error';
import { ArticleType } from '../domain/articleType';
import * as graphCMS from '../domain/graphcms';
import GuidePage, { GuidePageProps } from '../client/templates/common/guide-page/GuidePage';
import AdvInterstitial from '../client/components/adv-interstitial/AdvInterstitial';
import { RelatedArticleItem } from '../client/components/related-articles/RelatedArticles';
import { mapArticlesNodes } from '../client/graphql/mappers/articleMappers';
import { RELATED_NEWS_QUERY } from '../client/graphql/queries/news';
import { getLogger } from '../logger/isomorphic';
import { getRelated } from '../client/utils/article/articleUtils';
import { includes } from '../client/utils/graphcms/nodeUtils';
import { getBottomBarByAdvType } from '../services/bottom-bars/bottomBars';
import * as styles from '../client/templates/guide/Guide.style';
import { ARTICLE_PREVIEW_HEIGHT, ARTICLE_PREVIEW_WIDTH } from '../client/constants';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { withGetServerSideError, withError } from '../client/hocs/with-server-side-error/withServerSideError';
import { importDynamicData } from '../services/importDynamicData';

export interface GuideProps {
  guide: graphCMS.Guide,
  featured: Omit<RelatedArticleItem, 'onClick'>[],
  relatedNews: Omit<RelatedArticleItem, 'onClick'>[],
  bottomBar: GuidePageProps['bottomBar'],
  guideRef?: Pick<graphCMS.Guide, 'id' | 'title' | 'alternateTitle'>,
}

const logger = getLogger();

export const Guide: NextPage<GuideProps> = ({
  guide,
  guideRef,
  featured,
  relatedNews,
  bottomBar,
}) => (
  <GuidePage
    guide={guide}
    relatedGuides={featured}
    relatedNews={relatedNews}
    type={ArticleType.GUIDE}
    advInPage={(
      <div css={styles.interstitialAd}>
        <AdvInterstitial guideRef={guideRef} currentGuideId={guide.id} />
      </div>
      )}
    bottomBar={bottomBar}
  />
);

export const getServerSideProps = withGetServerSideError<GuideProps>(async ({ query }) => {
  if ((query.slug as string).includes('}')) {
    throw buildPageGone(`Page with slug ${query.slug} is gone`);
  }

  const graphQLClient = initGraphQLClient();

  const { advInterstitial: { guideRef } } = await importDynamicData('pages/guide.yml');

  const { data: { guide: guideRefObj } } = await graphQLClient.query<{ guide: graphCMS.Guide }>({
    query: GUIDE_QUERY,
    variables: {
      id: guideRef.id,
    },
  });

  const { data: { guide } } = await graphQLClient.query<{ guide: graphCMS.Guide }>({
    query: GUIDE_QUERY,
    variables: {
      id: query.id,
      metaPreviewWidth: ARTICLE_PREVIEW_WIDTH,
      metaPreviewHeight: ARTICLE_PREVIEW_HEIGHT,
    },
  });

  assertNotNullOrUndefined(guide, buildNotFound(`Guide with id "${query.id}" not found`));

  const slug = slugIt(guide.alternateTitle || guide.title);
  const redirectUrl = {
    ...query,
    id: guide.id,
    slug,
  };

  if (guide.target === graphCMS.Target.AGENTE) {
    return {
      redirect: {
        destination: buildArticleURLObject(redirectUrl, ArticleType.EDUCATION).as,
        permanent: true,
      },
    };
  }

  if (query.slug !== slug) {
    return {
      redirect: {
        destination: buildArticleURLObject(redirectUrl, ArticleType.GUIDE).as,
        permanent: true,
      },
    };
  }

  const [featured, relatedNews] = await Promise.all([
    graphQLClient
      .query({ query: FEATURED_GUIDES_QUERY, variables: { count: 4 } })
      .then(({ data }) => mapArticlesNodes(data.featured, ArticleType.GUIDE))
      .catch((err) => {
        logger.warn({ err }, 'Error while quering feature guides');
        return [];
      }),
    graphQLClient
      .query({
        query: RELATED_NEWS_QUERY,
        variables: {
          date: guide.published, count: 4, createdAt: guide.createdAt,
        },
      })
      .then(({ data }) => getRelated(data.older, data.newer, 4))
      .then((related) => mapArticlesNodes(related, ArticleType.NEWS))
      .catch((err) => {
        logger.warn({ err }, 'Error while quering related news');
        return [];
      }),
  ]);

  const mainColumn = [...guide.mainColumn];

  if (!includes(guide.mainColumn, graphCMS.NodeType.ADV_FLUID)) {
    mainColumn.splice(1, 0, {
      __typename: graphCMS.NodeType.ADV_FLUID,
      id: Date.now().toString(),
      advFluidType: graphCMS.AdvFluidType.SELL_PROPERTY,
      variant: graphCMS.AdvFluidVariant.VARIANT_1,
    } as graphCMS.AdvFluid);
  }

  const bottomBar = getBottomBarByAdvType(guide.rightColumn?.advFluidType ?? graphCMS.AdvFluidType.SELL_PROPERTY);

  return {
    props: {
      guide: {
        ...guide,
        mainColumn,
      },
      guideRef: guideRefObj
        ? {
          id: guideRefObj.id,
          title: guideRefObj.title,
          alternateTitle: guideRefObj.alternateTitle,
        }
        : null,
      featured,
      relatedNews,
      bottomBar,
    },
  };
});

export default withError(Guide);
